import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    ChevronDown, ChevronRight, User, LogOut, Settings as SettingsIcon, Users, Plus, Edit, Trash2,
    Search, Calendar, Clock, DollarSign, Heart, TrendingUp, Award, Star,
    Download, Share2, PieChart, BarChart3, Trophy, Gift, Smile, CheckCircle,
    Upload, Tag, X, Crown, Filter, Eye, Image as ImageIcon
} from 'lucide-react';
import { fetchCommunityOrganizations, createCommunityOrganization, updateCommunityOrganization, deleteCommunityOrganization } from '../services/communityApi';
import MemberAvatar from './MemberAvatar';
import MemberSelector from './MemberSelector';
import Premium from './Premium';

const CommunityAndGiving = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('tracker'); // tracker, dashboard, missions
    const [showFamilyDropdown, setShowFamilyDropdown] = useState(false);
    const [showUserMenu, setShowUserMenu] = useState(false);
    const [showPremiumModal, setShowPremiumModal] = useState(false);
    const [families, setFamilies] = useState([]);
    const [selectedFamily, setSelectedFamily] = useState(null);
    const dropdownRef = useRef(null);
    const userMenuRef = useRef(null);

    // Tracker state
    const [activities, setActivities] = useState([]);
    const [showAddActivityModal, setShowAddActivityModal] = useState(false);
    const [editingActivity, setEditingActivity] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterType, setFilterType] = useState('All');
    const [filterCategory, setFilterCategory] = useState('All');
    const [newActivity, setNewActivity] = useState({
        title: '',
        type: 'Donation',
        date: new Date().toISOString().split('T')[0],
        organization: '',
        amount: '',
        hours: '',
        notes: '',
        category: 'Community',
        attachments: [],
        recurring: false,
        recurringFrequency: 'Monthly',
        members: []
    });

    // Missions state
    const [missions, setMissions] = useState([]);
    const [showAddMissionModal, setShowAddMissionModal] = useState(false);
    const [editingMission, setEditingMission] = useState(null);
    const [newMission, setNewMission] = useState({
        title: '',
        description: '',
        badge: 'Kindness Star',
        difficulty: 'Easy',
        assignedTo: [],
        completed: false,
        completionDate: '',
        photos: []
    });

    // Load families
    useEffect(() => {
        const loadData = async () => {
            try {
                const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
                const response = await fetch(`${apiUrl}/v1/families`, {
                    credentials: 'include'
                });
                
                if (!response.ok) {
                    throw new Error('Failed to load families');
                }
                
                const familiesData = await response.json();
                setFamilies(familiesData);

                const selectedFamilyId = localStorage.getItem('selectedFamilyId');
                let familyToSelect = null;
                
                if (selectedFamilyId && familiesData.length > 0) {
                    familyToSelect = familiesData.find(f => f.id === selectedFamilyId);
                }
                
                if (!familyToSelect && familiesData.length === 1) {
                    familyToSelect = familiesData[0];
                    localStorage.setItem('selectedFamilyId', familiesData[0].id);
                }
                
                if (!familyToSelect && familiesData.length > 0) {
                    localStorage.removeItem('selectedFamilyId');
                }
                
                setSelectedFamily(familyToSelect);
            } catch (error) {
                console.error('Error loading families:', error);
            }
        };
        loadData();
    }, []);

    // Load community data when family changes
    useEffect(() => {
        const loadCommunityData = async () => {
            if (!selectedFamily) return;
            
            try {
                const response = await fetchCommunityOrganizations(selectedFamily.id);
                const orgs = response.items || [];
                
                // Separate by type
                setActivities(orgs.filter(o => o.type === 'activity'));
                setMissions(orgs.filter(o => o.type === 'mission'));
            } catch (error) {
                console.error('Error loading community data:', error);
            }
        };
        loadCommunityData();
    }, [selectedFamily]);

    const saveActivities = (data) => {
        setActivities(data);
    };

    const saveMissions = (data) => {
        setMissions(data);
    };

    const handleFamilySelect = (family) => {
        setSelectedFamily(family);
        localStorage.setItem('selectedFamilyId', family.id);
        setShowFamilyDropdown(false);
    };

    const handleLogout = () => {
        localStorage.removeItem('hasLoggedIn');
        localStorage.removeItem('selectedFamilyId');
        navigate('/login');
    };

    // Click outside handlers
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowFamilyDropdown(false);
            }
            if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
                setShowUserMenu(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Activity functions
    const handleAddActivity = async () => {
        if (!newActivity.title.trim()) {
            alert('Please enter a title');
            return;
        }

        if (!selectedFamily) {
            alert('Please select a family first');
            return;
        }

        try {
            const orgData = {
                type: 'activity',
                name: newActivity.title,
                activity_type: newActivity.type,
                category: newActivity.category,
                date: newActivity.date,
                organization_name: newActivity.organization || null,
                amount: newActivity.amount || null,
                hours: newActivity.hours || null,
                notes: newActivity.notes || null,
                data: {
                    attachments: newActivity.attachments || [],
                    recurring: newActivity.recurring,
                    recurringFrequency: newActivity.recurringFrequency,
                    members: newActivity.members || []
                }
            };

            const created = await createCommunityOrganization(selectedFamily.id, orgData);
            saveActivities([...activities, created]);
            resetActivityForm();
            setShowAddActivityModal(false);
        } catch (error) {
            console.error('Error adding activity:', error);
            alert(`Failed to add activity: ${error.message}`);
        }
    };

    const handleEditActivity = (activity) => {
        setEditingActivity(activity);
        setNewActivity(activity);
        setShowAddActivityModal(true);
    };

    const handleUpdateActivity = async () => {
        if (!selectedFamily || !editingActivity) return;

        try {
            const orgData = {
                type: 'activity',
                name: newActivity.title,
                activity_type: newActivity.type,
                category: newActivity.category,
                date: newActivity.date,
                organization_name: newActivity.organization || null,
                amount: newActivity.amount || null,
                hours: newActivity.hours || null,
                notes: newActivity.notes || null,
                data: {
                    attachments: newActivity.attachments || [],
                    recurring: newActivity.recurring,
                    recurringFrequency: newActivity.recurringFrequency,
                    members: newActivity.members || []
                }
            };

            const updated = await updateCommunityOrganization(selectedFamily.id, editingActivity.id, orgData);
            saveActivities(activities.map(a => a.id === editingActivity.id ? updated : a));
            resetActivityForm();
            setShowAddActivityModal(false);
            setEditingActivity(null);
        } catch (error) {
            console.error('Error updating activity:', error);
            alert(`Failed to update activity: ${error.message}`);
        }
    };

    const handleDeleteActivity = async (id) => {
        if (!window.confirm('Are you sure you want to delete this activity?')) {
            return;
        }

        if (!selectedFamily) {
            alert('Please select a family first');
            return;
        }

        try {
            await deleteCommunityOrganization(selectedFamily.id, id);
            saveActivities(activities.filter(a => a.id !== id));
        } catch (error) {
            console.error('Error deleting activity:', error);
            alert(`Failed to delete activity: ${error.message}`);
        }
    };

    const resetActivityForm = () => {
        setNewActivity({
            title: '',
            type: 'Donation',
            date: new Date().toISOString().split('T')[0],
            organization: '',
            amount: '',
            hours: '',
            notes: '',
            category: 'Community',
            attachments: [],
            recurring: false,
            recurringFrequency: 'Monthly',
            members: []
        });
    };

    // Mission functions
    const handleAddMission = async () => {
        if (!newMission.title.trim()) {
            alert('Please enter a mission title');
            return;
        }

        if (!selectedFamily) {
            alert('Please select a family first');
            return;
        }

        try {
            const orgData = {
                type: 'mission',
                name: newMission.title,
                description: newMission.description || null,
                data: {
                    badge: newMission.badge,
                    difficulty: newMission.difficulty,
                    assignedTo: newMission.assignedTo || [],
                    completed: newMission.completed,
                    completionDate: newMission.completionDate || null,
                    photos: newMission.photos || []
                }
            };

            const created = await createCommunityOrganization(selectedFamily.id, orgData);
            saveMissions([...missions, created]);
            resetMissionForm();
            setShowAddMissionModal(false);
        } catch (error) {
            console.error('Error adding mission:', error);
            alert(`Failed to add mission: ${error.message}`);
        }
    };

    const handleEditMission = (mission) => {
        setEditingMission(mission);
        setNewMission(mission);
        setShowAddMissionModal(true);
    };

    const handleUpdateMission = async () => {
        if (!selectedFamily || !editingMission) return;

        try {
            const orgData = {
                type: 'mission',
                name: newMission.title,
                description: newMission.description || null,
                data: {
                    badge: newMission.badge,
                    difficulty: newMission.difficulty,
                    assignedTo: newMission.assignedTo || [],
                    completed: newMission.completed,
                    completionDate: newMission.completionDate || null,
                    photos: newMission.photos || []
                }
            };

            const updated = await updateCommunityOrganization(selectedFamily.id, editingMission.id, orgData);
            saveMissions(missions.map(m => m.id === editingMission.id ? updated : m));
            resetMissionForm();
            setShowAddMissionModal(false);
            setEditingMission(null);
        } catch (error) {
            console.error('Error updating mission:', error);
            alert(`Failed to update mission: ${error.message}`);
        }
    };

    const handleDeleteMission = async (id) => {
        if (!window.confirm('Are you sure you want to delete this mission?')) {
            return;
        }

        if (!selectedFamily) {
            alert('Please select a family first');
            return;
        }

        try {
            await deleteCommunityOrganization(selectedFamily.id, id);
            saveMissions(missions.filter(m => m.id !== id));
        } catch (error) {
            console.error('Error deleting mission:', error);
            alert(`Failed to delete mission: ${error.message}`);
        }
    };

    const handleCompleteMission = (id) => {
        const updated = missions.map(m => {
            if (m.id === id) {
                return { 
                    ...m, 
                    completed: true, 
                    completionDate: new Date().toISOString()
                };
            }
            return m;
        });
        saveMissions(updated);
    };

    const resetMissionForm = () => {
        setNewMission({
            title: '',
            description: '',
            badge: 'Kindness Star',
            difficulty: 'Easy',
            assignedTo: [],
            completed: false,
            completionDate: '',
            photos: []
        });
    };

    // Calculate dashboard metrics
    const calculateMetrics = () => {
        const totalDonations = activities
            .filter(a => a.type === 'Donation' && a.amount)
            .reduce((sum, a) => sum + parseFloat(a.amount || 0), 0);

        const totalHours = activities
            .filter(a => (a.type === 'Volunteering' || a.hours) && a.hours)
            .reduce((sum, a) => sum + parseFloat(a.hours || 0), 0);

        const causesSupported = new Set(activities.map(a => a.category)).size;

        const completedMissions = missions.filter(m => m.completed).length;

        // Category breakdown
        const categoryBreakdown = activities.reduce((acc, a) => {
            acc[a.category] = (acc[a.category] || 0) + 1;
            return acc;
        }, {});

        // Monthly donations
        const monthlyData = {};
        activities.forEach(a => {
            if (a.amount) {
                const month = new Date(a.date).toLocaleString('default', { month: 'short' });
                monthlyData[month] = (monthlyData[month] || 0) + parseFloat(a.amount);
            }
        });

        return {
            totalDonations,
            totalHours,
            causesSupported,
            completedMissions,
            categoryBreakdown,
            monthlyData
        };
    };

    const metrics = calculateMetrics();

    // Filter activities
    const filteredActivities = activities.filter(activity => {
        const matchesSearch = activity.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                              activity.organization.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesType = filterType === 'All' || activity.type === filterType;
        const matchesCategory = filterCategory === 'All' || activity.category === filterCategory;
        return matchesSearch && matchesType && matchesCategory;
    });

    // Predefined mission templates
    const missionTemplates = [
        { title: 'Donate Old Toys', description: 'Collect and donate toys you no longer use', badge: 'Kindness Star', difficulty: 'Easy' },
        { title: 'Help in Cleaning Drive', description: 'Participate in a community cleaning activity', badge: 'Eco Helper', difficulty: 'Medium' },
        { title: 'Feed Stray Animals', description: 'Feed stray dogs or cats in your neighborhood', badge: 'Animal Friend', difficulty: 'Easy' },
        { title: 'Plant a Tree', description: 'Plant and care for a tree', badge: 'Eco Hero', difficulty: 'Medium' },
        { title: 'Help an Elderly', description: 'Help an elderly person with groceries or chores', badge: 'Helper Star', difficulty: 'Easy' }
    ];

    const categories = ['Health', 'Education', 'Environment', 'Community', 'Animals', 'Others'];
    const badges = ['Kindness Star', 'Eco Helper', 'Helper Star', 'Animal Friend', 'Eco Hero', 'Super Giver'];

    return (
        <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50">
            {/* Navigation Bar */}
            <nav className="bg-white shadow-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        <div className="flex items-center gap-4">
                            <button 
                                onClick={() => navigate('/dashboard')}
                                className="flex items-center space-x-3 hover:opacity-80 transition-opacity"
                            >
                                <img src="/logo.png" alt="mifamli logo" className="h-10 w-10" />
                                <span className="text-xl font-bold text-primary-blue">mifamli</span>
                            </button>

                            {/* Mi Famlis Dropdown */}
                            <div className="relative" ref={dropdownRef}>
                                <button
                                    onClick={() => setShowFamilyDropdown(!showFamilyDropdown)}
                                    className="flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-white shadow-md hover:shadow-lg transition-all"
                                    style={{
                                        background: 'linear-gradient(135deg, #3b82f6 0%, #ec4899 100%)'
                                    }}
                                >
                                    {selectedFamily ? (
                                        <>
                                            <img
                                                src={selectedFamily.photo}
                                                alt={selectedFamily.name}
                                                className="w-6 h-6 rounded-full object-cover"
                                            />
                                            <span>{selectedFamily.name}</span>
                                        </>
                                    ) : (
                                        <span>Mi Famlis</span>
                                    )}
                                    <ChevronDown className="w-4 h-4" />
                                </button>

                                {showFamilyDropdown && (
                                    <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-lg shadow-xl py-2 border border-gray-200">
                                        {families.map((family) => (
                                            <button
                                                key={family.id}
                                                onClick={() => handleFamilySelect(family)}
                                                className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center space-x-3"
                                            >
                                                {family.photo ? (
                                                    <img 
                                                        src={family.photo} 
                                                        alt={family.name}
                                                        className="w-8 h-8 rounded-full object-cover"
                                                    />
                                                ) : (
                                                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-pink-400 flex items-center justify-center">
                                                        <Users className="w-4 h-4 text-white" />
                                                    </div>
                                                )}
                                                <div>
                                                    <div className="font-medium">{family.name}</div>
                                                    {family.tagline && (
                                                        <div className="text-xs text-gray-500">{family.tagline}</div>
                                                    )}
                                                </div>
                                            </button>
                                        ))}
                                        <div className="border-t border-gray-200 mt-2 pt-2">
                                            <button
                                                onClick={() => navigate('/famlis')}
                                                className="w-full px-4 py-2 text-left hover:bg-gray-50 text-primary-blue font-medium"
                                            >
                                                + Add Family
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="flex items-center gap-3">
                            {/* User Avatar Menu */}
                            <div className="relative" ref={userMenuRef}>
                                <button
                                    onClick={() => setShowUserMenu(!showUserMenu)}
                                    className="flex items-center gap-2 hover:bg-gray-50 rounded-lg p-2 transition-colors"
                                >
                                    <div className="w-10 h-10 rounded-full overflow-hidden bg-gradient-to-br from-primary-blue to-primary-pink flex items-center justify-center text-white font-semibold">
                                        U
                                    </div>
                                </button>

                                {showUserMenu && (
                                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl py-1 border border-gray-100 z-50">
                                        <button className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700">
                                            <User className="w-5 h-5" />
                                            <span className="font-medium">Profile</span>
                                        </button>
                                        <button className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700">
                                            <SettingsIcon className="w-5 h-5" />
                                            <span className="font-medium">Settings</span>
                                        </button>
                                        <button
                                            onClick={() => setShowPremiumModal(true)}
                                            className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                                        >
                                            <Crown className="w-5 h-5 text-yellow-500" />
                                            <span className="font-medium">Upgrade to Premium</span>
                                        </button>
                                        <div className="border-t border-gray-100 my-1"></div>
                                        <button
                                            onClick={handleLogout}
                                            className="w-full px-4 py-3 text-left hover:bg-red-50 flex items-center gap-3 text-red-600"
                                        >
                                            <LogOut className="w-5 h-5" />
                                            <span className="font-medium">Logout</span>
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Main Content */}
            <div className="max-w-7xl mx-auto px-4 py-8">
                {/* Breadcrumb */}
                <div className="mb-4 flex items-center gap-2 text-sm">
                    <button 
                        onClick={() => navigate('/dashboard')}
                        className="text-gray-600 hover:text-primary-blue transition-colors"
                    >
                        Dashboard
                    </button>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-900 font-medium">Community & Giving</span>
                </div>

                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-4xl font-bold text-gray-900 mb-2">❤️ Community & Giving</h1>
                    <p className="text-gray-600">Track donations, volunteering, and teach kindness to your family</p>
                </div>

                {/* Tabs */}
                <div className="mb-8 border-b border-gray-200">
                    <div className="flex space-x-8">
                        <button
                            onClick={() => setActiveTab('tracker')}
                            className={`pb-4 px-2 font-medium transition-colors relative ${
                                activeTab === 'tracker'
                                    ? 'text-primary-blue'
                                    : 'text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            💝 Donation & Volunteering
                            {activeTab === 'tracker' && (
                                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-blue"></div>
                            )}
                        </button>
                        <button
                            onClick={() => setActiveTab('dashboard')}
                            className={`pb-4 px-2 font-medium transition-colors relative ${
                                activeTab === 'dashboard'
                                    ? 'text-primary-blue'
                                    : 'text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            📊 Family Impact
                            {activeTab === 'dashboard' && (
                                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-blue"></div>
                            )}
                        </button>
                        <button
                            onClick={() => setActiveTab('missions')}
                            className={`pb-4 px-2 font-medium transition-colors relative ${
                                activeTab === 'missions'
                                    ? 'text-primary-blue'
                                    : 'text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            🧒 Kindness Missions
                            {activeTab === 'missions' && (
                                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-blue"></div>
                            )}
                        </button>
                    </div>
                </div>

                {/* TRACKER TAB */}
                {activeTab === 'tracker' && (
                    <div>
                        {/* Search and Filters */}
                        <div className="flex justify-between items-center mb-6 flex-wrap gap-4">
                            <div className="flex items-center space-x-4 flex-1">
                                <div className="relative flex-1 max-w-md">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                    <input
                                        type="text"
                                        placeholder="Search activities..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                    />
                                </div>
                                <select
                                    value={filterType}
                                    onChange={(e) => setFilterType(e.target.value)}
                                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                >
                                    <option value="All">All Types</option>
                                    <option value="Donation">Donations</option>
                                    <option value="Volunteering">Volunteering</option>
                                    <option value="Cause">Causes</option>
                                </select>
                                <select
                                    value={filterCategory}
                                    onChange={(e) => setFilterCategory(e.target.value)}
                                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                >
                                    <option value="All">All Categories</option>
                                    {categories.map(cat => (
                                        <option key={cat} value={cat}>{cat}</option>
                                    ))}
                                </select>
                            </div>
                            <button
                                onClick={() => {
                                    resetActivityForm();
                                    setEditingActivity(null);
                                    setShowAddActivityModal(true);
                                }}
                                className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-green-600 to-blue-600 text-white rounded-lg hover:opacity-90 transition-opacity"
                            >
                                <Plus className="w-5 h-5" />
                                <span>Add Activity</span>
                            </button>
                        </div>

                        {/* Activities List */}
                        <div className="space-y-4">
                            {filteredActivities.map((activity) => (
                                <div key={activity.id} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
                                    <div className="flex justify-between items-start mb-4">
                                        <div className="flex-1">
                                            <div className="flex items-center space-x-3 mb-2">
                                                <h3 className="text-xl font-bold text-gray-900">{activity.title}</h3>
                                                <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                                                    activity.type === 'Donation' ? 'bg-green-100 text-green-800' :
                                                    activity.type === 'Volunteering' ? 'bg-blue-100 text-blue-800' :
                                                    'bg-purple-100 text-purple-800'
                                                }`}>
                                                    {activity.type}
                                                </span>
                                                <span className="px-3 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                                                    {activity.category}
                                                </span>
                                            </div>
                                            <div className="flex items-center space-x-4 text-sm text-gray-600 mb-2">
                                                <span className="flex items-center">
                                                    <Calendar className="w-4 h-4 mr-1" />
                                                    {new Date(activity.date).toLocaleDateString()}
                                                </span>
                                                {activity.organization && (
                                                    <span>{activity.organization}</span>
                                                )}
                                                {activity.amount && (
                                                    <span className="flex items-center font-semibold text-green-600">
                                                        <DollarSign className="w-4 h-4" />
                                                        {activity.amount}
                                                    </span>
                                                )}
                                                {activity.hours && (
                                                    <span className="flex items-center font-semibold text-blue-600">
                                                        <Clock className="w-4 h-4 mr-1" />
                                                        {activity.hours} hrs
                                                    </span>
                                                )}
                                            </div>
                                            {activity.notes && (
                                                <p className="text-gray-700 text-sm">{activity.notes}</p>
                                            )}
                                            {activity.recurring && (
                                                <div className="mt-2">
                                                    <span className="inline-flex items-center px-2 py-1 text-xs bg-orange-100 text-orange-800 rounded-full">
                                                        🔄 Recurring {activity.recurringFrequency}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex space-x-2">
                                            <button
                                                onClick={() => handleEditActivity(activity)}
                                                className="p-2 hover:bg-gray-100 rounded"
                                            >
                                                <Edit className="w-5 h-5 text-gray-600" />
                                            </button>
                                            <button
                                                onClick={() => handleDeleteActivity(activity.id)}
                                                className="p-2 hover:bg-gray-100 rounded"
                                            >
                                                <Trash2 className="w-5 h-5 text-red-600" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {filteredActivities.length === 0 && (
                            <div className="text-center py-12">
                                <Heart className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                                <p className="text-gray-500 mb-4">No activities found</p>
                                <button
                                    onClick={() => setShowAddActivityModal(true)}
                                    className="text-green-600 hover:underline"
                                >
                                    Add your first activity
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {/* DASHBOARD TAB */}
                {activeTab === 'dashboard' && (
                    <div>
                        {/* Summary Cards */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                            <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg p-6 text-white">
                                <div className="flex items-center justify-between mb-2">
                                    <DollarSign className="w-8 h-8" />
                                    <TrendingUp className="w-5 h-5" />
                                </div>
                                <p className="text-sm opacity-90 mb-1">Total Donations</p>
                                <p className="text-3xl font-bold">₹{metrics.totalDonations.toLocaleString()}</p>
                            </div>

                            <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-6 text-white">
                                <div className="flex items-center justify-between mb-2">
                                    <Clock className="w-8 h-8" />
                                    <TrendingUp className="w-5 h-5" />
                                </div>
                                <p className="text-sm opacity-90 mb-1">Volunteer Hours</p>
                                <p className="text-3xl font-bold">{metrics.totalHours} hrs</p>
                            </div>

                            <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg p-6 text-white">
                                <div className="flex items-center justify-between mb-2">
                                    <Heart className="w-8 h-8" />
                                    <Star className="w-5 h-5" />
                                </div>
                                <p className="text-sm opacity-90 mb-1">Causes Supported</p>
                                <p className="text-3xl font-bold">{metrics.causesSupported}</p>
                            </div>

                            <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg p-6 text-white">
                                <div className="flex items-center justify-between mb-2">
                                    <Award className="w-8 h-8" />
                                    <Trophy className="w-5 h-5" />
                                </div>
                                <p className="text-sm opacity-90 mb-1">Kids' Missions</p>
                                <p className="text-3xl font-bold">{metrics.completedMissions}</p>
                            </div>
                        </div>

                        {/* Charts Section */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                            {/* Category Breakdown */}
                            <div className="bg-white rounded-lg shadow-md p-6">
                                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                                    <PieChart className="w-6 h-6 mr-2 text-purple-600" />
                                    Categories Breakdown
                                </h3>
                                <div className="space-y-3">
                                    {Object.entries(metrics.categoryBreakdown).map(([category, count]) => (
                                        <div key={category} className="flex items-center justify-between">
                                            <span className="text-gray-700">{category}</span>
                                            <div className="flex items-center space-x-2">
                                                <div className="w-32 bg-gray-200 rounded-full h-2">
                                                    <div 
                                                        className="bg-gradient-to-r from-purple-500 to-pink-500 h-2 rounded-full"
                                                        style={{ width: `${(count / activities.length) * 100}%` }}
                                                    ></div>
                                                </div>
                                                <span className="text-sm font-semibold text-gray-900 w-8">{count}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* This Year's Impact */}
                            <div className="bg-white rounded-lg shadow-md p-6">
                                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                                    <BarChart3 className="w-6 h-6 mr-2 text-green-600" />
                                    This Year's Impact
                                </h3>
                                <div className="space-y-4">
                                    <div className="border-l-4 border-green-500 pl-4">
                                        <p className="text-sm text-gray-600">Total Activities</p>
                                        <p className="text-2xl font-bold text-gray-900">{activities.length}</p>
                                    </div>
                                    <div className="border-l-4 border-blue-500 pl-4">
                                        <p className="text-sm text-gray-600">Active Volunteers</p>
                                        <p className="text-2xl font-bold text-gray-900">{selectedFamily?.members?.length || 0}</p>
                                    </div>
                                    <div className="border-l-4 border-purple-500 pl-4">
                                        <p className="text-sm text-gray-600">Organizations Helped</p>
                                        <p className="text-2xl font-bold text-gray-900">
                                            {new Set(activities.map(a => a.organization).filter(Boolean)).size}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Export Actions */}
                        <div className="bg-white rounded-lg shadow-md p-6">
                            <h3 className="text-xl font-bold text-gray-900 mb-4">Export & Share</h3>
                            <div className="flex space-x-4">
                                <button className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                                    <Download className="w-5 h-5" />
                                    <span>Download PDF Report</span>
                                </button>
                                <button className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
                                    <Share2 className="w-5 h-5" />
                                    <span>Share Summary</span>
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* MISSIONS TAB */}
                {activeTab === 'missions' && (
                    <div>
                        {/* Weekly Kindness Tip */}
                        <div className="bg-gradient-to-r from-yellow-100 to-orange-100 rounded-lg p-6 mb-8">
                            <div className="flex items-start space-x-4">
                                <Smile className="w-8 h-8 text-orange-600 flex-shrink-0 mt-1" />
                                <div>
                                    <h3 className="text-lg font-bold text-gray-900 mb-2">💡 Weekly Kindness Tip</h3>
                                    <p className="text-gray-800">
                                        This week, try helping a neighbor with their groceries or yard work. Small acts of kindness make a big difference!
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Mission Templates */}
                        <div className="mb-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-3">Quick Mission Templates</h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3">
                                {missionTemplates.map((template, idx) => (
                                    <button
                                        key={idx}
                                        onClick={() => {
                                            setNewMission({ ...newMission, ...template });
                                            setShowAddMissionModal(true);
                                        }}
                                        className="p-3 bg-white border-2 border-dashed border-gray-300 rounded-lg hover:border-green-500 hover:bg-green-50 transition-colors text-left"
                                    >
                                        <p className="text-sm font-medium text-gray-900">{template.title}</p>
                                        <p className="text-xs text-gray-600 mt-1">{template.badge}</p>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Add Mission Button */}
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-xl font-bold text-gray-900">Active Missions</h3>
                            <button
                                onClick={() => {
                                    resetMissionForm();
                                    setEditingMission(null);
                                    setShowAddMissionModal(true);
                                }}
                                className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-orange-600 to-pink-600 text-white rounded-lg hover:opacity-90"
                            >
                                <Plus className="w-5 h-5" />
                                <span>Create Mission</span>
                            </button>
                        </div>

                        {/* Missions Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {missions.map((mission) => (
                                <div key={mission.id} className={`bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow ${mission.completed ? 'opacity-75' : ''}`}>
                                    <div className="flex justify-between items-start mb-3">
                                        <div className="flex-1">
                                            <h3 className="text-lg font-bold text-gray-900 mb-2">{mission.title}</h3>
                                            <div className="flex items-center space-x-2 mb-2">
                                                <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                                    mission.difficulty === 'Easy' ? 'bg-green-100 text-green-800' :
                                                    mission.difficulty === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                                                    'bg-red-100 text-red-800'
                                                }`}>
                                                    {mission.difficulty}
                                                </span>
                                                <span className="px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">
                                                    🏆 {mission.badge}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="flex space-x-1">
                                            <button
                                                onClick={() => handleEditMission(mission)}
                                                className="p-1 hover:bg-gray-100 rounded"
                                            >
                                                <Edit className="w-4 h-4 text-gray-600" />
                                            </button>
                                            <button
                                                onClick={() => handleDeleteMission(mission.id)}
                                                className="p-1 hover:bg-gray-100 rounded"
                                            >
                                                <Trash2 className="w-4 h-4 text-red-600" />
                                            </button>
                                        </div>
                                    </div>

                                    <p className="text-sm text-gray-600 mb-4">{mission.description}</p>

                                    {mission.completed ? (
                                        <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center space-x-2">
                                            <CheckCircle className="w-5 h-5 text-green-600" />
                                            <span className="text-sm text-green-800 font-medium">
                                                Completed! {new Date(mission.completionDate).toLocaleDateString()}
                                            </span>
                                        </div>
                                    ) : (
                                        <button
                                            onClick={() => handleCompleteMission(mission.id)}
                                            className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                                        >
                                            <CheckCircle className="w-5 h-5" />
                                            <span>Mark as Complete</span>
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>

                        {missions.length === 0 && (
                            <div className="text-center py-12">
                                <Gift className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                                <p className="text-gray-500 mb-4">No missions yet</p>
                                <button
                                    onClick={() => setShowAddMissionModal(true)}
                                    className="text-orange-600 hover:underline"
                                >
                                    Create your first kindness mission
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Add/Edit Activity Modal */}
            {showAddActivityModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
                    <div className="bg-white rounded-lg max-w-2xl w-full max-h-screen overflow-y-auto">
                        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                            <h2 className="text-2xl font-bold">
                                {editingActivity ? 'Edit Activity' : 'Add New Activity'}
                            </h2>
                            <button
                                onClick={() => {
                                    setShowAddActivityModal(false);
                                    setEditingActivity(null);
                                    resetActivityForm();
                                }}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        <div className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                                <input
                                    type="text"
                                    value={newActivity.title}
                                    onChange={(e) => setNewActivity({ ...newActivity, title: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                    placeholder="e.g., Monthly NGO Donation, Tree Plantation Drive"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                                    <select
                                        value={newActivity.type}
                                        onChange={(e) => setNewActivity({ ...newActivity, type: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                    >
                                        <option value="Donation">Donation</option>
                                        <option value="Volunteering">Volunteering</option>
                                        <option value="Cause">Cause</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                                    <select
                                        value={newActivity.category}
                                        onChange={(e) => setNewActivity({ ...newActivity, category: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                    >
                                        {categories.map(cat => (
                                            <option key={cat} value={cat}>{cat}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                                    <input
                                        type="date"
                                        value={newActivity.date}
                                        onChange={(e) => setNewActivity({ ...newActivity, date: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Organization</label>
                                    <input
                                        type="text"
                                        value={newActivity.organization}
                                        onChange={(e) => setNewActivity({ ...newActivity, organization: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                        placeholder="Organization name"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Amount (₹)</label>
                                    <input
                                        type="number"
                                        value={newActivity.amount}
                                        onChange={(e) => setNewActivity({ ...newActivity, amount: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                        placeholder="Amount donated"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Hours</label>
                                    <input
                                        type="number"
                                        value={newActivity.hours}
                                        onChange={(e) => setNewActivity({ ...newActivity, hours: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                        placeholder="Hours volunteered"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                                <textarea
                                    value={newActivity.notes}
                                    onChange={(e) => setNewActivity({ ...newActivity, notes: e.target.value })}
                                    rows={3}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                    placeholder="Additional notes about this activity"
                                />
                            </div>

                            <div className="flex items-center space-x-2">
                                <input
                                    type="checkbox"
                                    id="recurring"
                                    checked={newActivity.recurring}
                                    onChange={(e) => setNewActivity({ ...newActivity, recurring: e.target.checked })}
                                    className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                                />
                                <label htmlFor="recurring" className="text-sm font-medium text-gray-700">
                                    Recurring Activity
                                </label>
                            </div>

                            {newActivity.recurring && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Frequency</label>
                                    <select
                                        value={newActivity.recurringFrequency}
                                        onChange={(e) => setNewActivity({ ...newActivity, recurringFrequency: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                    >
                                        <option value="Weekly">Weekly</option>
                                        <option value="Monthly">Monthly</option>
                                        <option value="Yearly">Yearly</option>
                                    </select>
                                </div>
                            )}
                        </div>

                        <div className="sticky bottom-0 bg-gray-50 px-6 py-4 flex justify-end space-x-3 border-t border-gray-200">
                            <button
                                onClick={() => {
                                    setShowAddActivityModal(false);
                                    setEditingActivity(null);
                                    resetActivityForm();
                                }}
                                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={editingActivity ? handleUpdateActivity : handleAddActivity}
                                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                            >
                                {editingActivity ? 'Update' : 'Add'} Activity
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add/Edit Mission Modal */}
            {showAddMissionModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
                    <div className="bg-white rounded-lg max-w-2xl w-full max-h-screen overflow-y-auto">
                        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                            <h2 className="text-2xl font-bold">
                                {editingMission ? 'Edit Mission' : 'Create Kindness Mission'}
                            </h2>
                            <button
                                onClick={() => {
                                    setShowAddMissionModal(false);
                                    setEditingMission(null);
                                    resetMissionForm();
                                }}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        <div className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Mission Title *</label>
                                <input
                                    type="text"
                                    value={newMission.title}
                                    onChange={(e) => setNewMission({ ...newMission, title: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                                    placeholder="e.g., Donate Old Toys, Help Clean the Park"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                                <textarea
                                    value={newMission.description}
                                    onChange={(e) => setNewMission({ ...newMission, description: e.target.value })}
                                    rows={3}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                                    placeholder="Describe what needs to be done"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Badge</label>
                                    <select
                                        value={newMission.badge}
                                        onChange={(e) => setNewMission({ ...newMission, badge: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                                    >
                                        {badges.map(badge => (
                                            <option key={badge} value={badge}>{badge}</option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Difficulty</label>
                                    <select
                                        value={newMission.difficulty}
                                        onChange={(e) => setNewMission({ ...newMission, difficulty: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                                    >
                                        <option value="Easy">Easy</option>
                                        <option value="Medium">Medium</option>
                                        <option value="Hard">Hard</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        <div className="sticky bottom-0 bg-gray-50 px-6 py-4 flex justify-end space-x-3 border-t border-gray-200">
                            <button
                                onClick={() => {
                                    setShowAddMissionModal(false);
                                    setEditingMission(null);
                                    resetMissionForm();
                                }}
                                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={editingMission ? handleUpdateMission : handleAddMission}
                                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
                            >
                                {editingMission ? 'Update' : 'Create'} Mission
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Premium Modal */}
            {showPremiumModal && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black bg-opacity-75">
                    <div className="relative rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col" style={{ background: 'transparent' }}>
                        {/* Close Button */}
                        <button
                            onClick={() => setShowPremiumModal(false)}
                            className="absolute top-4 right-4 z-50 p-2 bg-white/90 backdrop-blur-sm rounded-full shadow-lg hover:bg-white transition-colors"
                        >
                            <X className="w-6 h-6 text-gray-600" />
                        </button>
                        
                        {/* Premium Component */}
                        <Premium isModal={true} />
                    </div>
                </div>
            )}
        </div>
    );
};

export default CommunityAndGiving;

